import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { FieldGroup } from '../../../location-utils';
import { EXPORTABLE_FIELD_IDS } from '../../../constants';
import {
  BulkActionPopupComponent,
  BulkActionPopupData,
  SelectedFieldsRadioButton,
  SelectedLocationsRadioButton,
} from '../bulk-action-popup/bulk-action-popup.component';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { NgClass } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatIcon } from '@angular/material/icon';


@Component({
    selector: 'app-export-locations-popup',
    templateUrl: './export-locations-popup.component.html',
    styleUrls: ['../bulk-action-popup/bulk-action-popup.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatDialogTitle,
        MatIcon,
        MatDialogContent,
        MatRadioGroup,
        MatRadioButton,
        MatCheckbox,
        NgClass,
        MatDialogActions,
        MatButton,
        MatDialogClose,
        TranslateModule,
    ],
})
export class ExportLocationsPopupComponent extends BulkActionPopupComponent implements  OnInit {

  private readonly gmbRequiredFields: string[] = ['businessName', 'city', 'addressLine1', 'country', 'primaryCategory'];
  private readonly gxpertsLabelParam = 'gxpertsLocationLabel';

  protected override readonly columns = [
    [FieldGroup.DEFAULT_DISPLAY, FieldGroup.OPENING_HOURS],
    [FieldGroup.ADDRESS, FieldGroup.MEDIA],
    [FieldGroup.OTHER, FieldGroup.PLACE_ACTION_LINKS],
    [FieldGroup.GMB_SPECIFIC, FieldGroup.BLS_SPECIFIC],
  ];

  protected override readonly selectedFieldsRadioDefault = SelectedFieldsRadioButton.ALL;

  protected override readonly selectedFieldsRadios: SelectedFieldsRadioButton[] = [
    SelectedFieldsRadioButton.ALL,
    SelectedFieldsRadioButton.GMB_REQUIRED_FIELDS,
    SelectedFieldsRadioButton.VISIBLE_ONLY,
    SelectedFieldsRadioButton.NONE,
  ];
  // radio buttons that will be rendered
  protected override readonly selectedLocationsRadios: SelectedLocationsRadioButton[] = [
    SelectedLocationsRadioButton.ALL,
    SelectedLocationsRadioButton.FILTERED,
    SelectedLocationsRadioButton.SELECTED,
  ];

  constructor(
      public dialogRef: MatDialogRef<ExportLocationsPopupComponent>,
      @Inject(MAT_DIALOG_DATA) public data: BulkActionPopupData,
      protected fb: FormBuilder,
  ) {
    super(data, dialogRef, fb);
  }

  /**
   * Handle FC 'valueChanges' event
   * Select 'labels' field whenever gxpertsLabel is selected
   */
  private initGxpertsLabelHandler() {
    this.getGxpertsLabelFC().valueChanges
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((val) => {
          if (val) {
            this.selectLabels();
          }
        });
  }

  /**
   * Handle FC 'valueChanges' event
   * Deselect 'gxpertsLabel' field whenever 'labels' is deselected
   */
  private initLabelsHandler() {
    this.getLabelsFC().valueChanges
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((val) => {
          if (!val) {
            this.deselectGxpertsLabel();
          }
        });
  }

  /************************************************************************************************/
  /*                                         OVERRIDES                                            */
  /************************************************************************************************/

  override ngOnInit(): void {
    this.getSelectableFields = () => EXPORTABLE_FIELD_IDS; // init method first
    super.ngOnInit(); // run parent 'onInit' logic
  }

  protected override buildForm() {
    super.buildForm();

    // Init gxpertsLabel FC.
    // Special label that should be added to each selected location, if user chooses so
    // if selected, also selects 'labels' field, unselecting 'labels' will unselect this option
    // will be added to field selection when generating API payload.
    // Added in BLS-3999
    this.bulkActionForm.addControl('gxpertsLabel', new FormControl<boolean>(false));
  }

  protected override initFCHandlers() {
    super.initFCHandlers();
    this.initGxpertsLabelHandler();
    this.initLabelsHandler();
  }

  protected override onSelectedFieldsRadioValueChange(radio: SelectedFieldsRadioButton) {
    super.onSelectedFieldsRadioValueChange(radio);

    if (radio === SelectedFieldsRadioButton.GMB_REQUIRED_FIELDS) {
      this.gmbRequiredToggle();
      return;
    }
  }

  protected override updateSelectedFieldsRadioValue(): string {
    super.updateSelectedFieldsRadioValue();

    const selectedFieldIDs = this.getSelectedFieldIDs();
    if (selectedFieldIDs.every((fieldID) => this.isRequiredOnImport(fieldID)) &&
        selectedFieldIDs.length === this.gmbRequiredFields.length) {
      this.getSelectedFieldsRadioFC().setValue(SelectedFieldsRadioButton.GMB_REQUIRED_FIELDS, { emitEvent: false });
      return;
    }
  }

  /**
   * Generates the return result from the dialog to the parent component.
   */
  protected override generateResult() {
    let selectedCsvFields = [];
    this.getSelectedFieldIDs().forEach(fieldID => {
      selectedCsvFields.push(this.getCsvHeadersForFieldId(fieldID));
    });

    // Append gxpertsLabel if selected
    if (this.getGxpertsLabelFC().value) {
      selectedCsvFields.push(this.gxpertsLabelParam);
    }

    return {
      subsetType: this.getSelectedLocationsRadioFC()?.value, // all, selected or filtered
      selectedCsvFields: selectedCsvFields.flat(),
    };
  }

  /************************************************************************************************/
  /*                                         HELPERS                                              */
  /************************************************************************************************/

  /**
   * Selects all fields that are required for the GMB import and deselects those which aren't
   */
  private gmbRequiredToggle() {
    this.getSelectedFieldsFormArray()?.controls.forEach((fc, i) => {
      fc.setValue(this.isRequiredOnImport(this.getSelectableFieldIDByIndex(i)), { emitEvent: false });
    });
  }

  /**
   * Select 'labels' field
   */
  private selectLabels() {
    this.getSelectedFieldsFormArray()?.controls[this.getSelectableFieldIndexByFieldID('labels')]?.setValue(true);
  };

  /**
   * Deselected 'gxpertsLabel'
   */
  private deselectGxpertsLabel() {
    this.getGxpertsLabelFC()?.setValue(false);
  }

  /**
   * Returns if a field is required on import.
   * @param fieldID ID of the field that needs to be checked
   */
  protected isRequiredOnImport(fieldID: string): boolean {
    return this.gmbRequiredFields.includes(fieldID);
  }

  /**
   * Returns the CSV header(s) that will be added to the CSV file if the field is chosen to be exported.
   * @param fieldId ID of the exportableField
   */
  private getCsvHeadersForFieldId(fieldId: string): string[] {
    switch (fieldId) {
      case 'businessHours':
        return [
          'sundayHours',
          'mondayHours',
          'tuesdayHours',
          'wednesdayHours',
          'thursdayHours',
          'fridayHours',
          'saturdayHours',
        ];
      case 'additionalCategory':
        return ['additionalCategories'];
      case 'additionalPhone':
        return ['additionalPhones'];
      case 'coordinate':
        return ['coordinate.latitude', 'coordinate.longitude'];
      case 'coordinateImport':
        return ['coordinateImport.latitude', 'coordinateImport.longitude'];
      case 'existsInGMB':
        return ['foundInMyBusiness'];
      case 'differentInGMB':
        return ['differentInMyBusiness'];
      default:
        return [fieldId];
    }
  }

  /**
   * Get 'gxpertsLabel' Form Control
   * @private
   */
  private getGxpertsLabelFC(): FormControl<boolean> {
    return this.bulkActionForm.get('gxpertsLabel') as FormControl<boolean>;
  }

  /**
   * Get 'labels' Form Control
   * @private
   */
  private getLabelsFC(): FormControl<boolean> {
    return this.getSelectedFieldsFormArray()?.controls[this.getSelectableFieldIndexByFieldID('labels')];
  }
}
